import React from "react";
import { Link } from "react-router-dom";
import { useSiteContent } from "../content/SiteContentContext.jsx";

// Aviso de privacidad integral (LFPDPPP). Texto FIJO en código, no editable desde el panel de
// contenido: identifica a la titular de la marca (misma persona que la constancia fiscal) y es lo
// que revisa la verificación de negocio de Meta. Solo el correo y el teléfono salen del contenido.
const RESPONSABLE = "Adelaida Fernandez Ramos";
const DOMICILIO = "Paseo de los Jazmines #303 L-4, Villa de Álvarez, Colima, México, C.P. 28984";
const ULTIMA_ACTUALIZACION = "29 de septiembre de 2026";

const Section = ({ icon, title, children }) => (
  <section className="content-card fade-up p-7 md:p-9">
    <div className="section-title mb-5">
      <span className="st-ico">
        <i className={`fa-solid ${icon}`} />
      </span>
      {title}
    </div>
    <div className="text-tPrimary/85 leading-relaxed space-y-3">{children}</div>
  </section>
);

const List = ({ items }) => (
  <ul className="brand-list space-y-2">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

const AvisoPrivacidad = () => {
  const { footer } = useSiteContent();
  const email = footer?.email || "contacto@incap.edu.mx";
  const phone = footer?.phone || "(312) 339 85 73";

  return (
    <div className="w-full overflow-hidden footerAdjust">
      <section className="brand-gradient text-white relative overflow-hidden">
        <div className="blob-glow w-[420px] h-[420px] -top-40 -right-24" />
        <div className="mx-auto w-full max-w-[900px] px-6 py-16 md:py-20 text-center relative z-[1]">
          <span className="eyebrow on-dark justify-center">Legal</span>
          <h1 className="mt-4 font-bold text-[34px] ss:text-[46px] leading-[1.1]">Aviso de privacidad</h1>
          <p className="mt-4 text-white/80 text-lg">
            Cómo tratamos y protegemos tus datos personales en INCAP.
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1000px] px-6 py-16 flex flex-col gap-6">
        <Section icon="fa-user-shield" title="Responsable del tratamiento">
          <p>
            <strong>{RESPONSABLE}</strong>, persona física que opera la marca <strong>INCAP</strong> (Instituto de
            Capacitación y Aprendizaje Permanente), con domicilio para oír y recibir notificaciones en {DOMICILIO}, es
            responsable del tratamiento de tus datos personales conforme a la Ley Federal de Protección de Datos
            Personales en Posesión de los Particulares y demás normativa aplicable.
          </p>
          <p>
            Para cualquier asunto relacionado con tus datos personales puedes escribirnos a{" "}
            <a className="text-primary font-medium" href={`mailto:${email}`}>{email}</a> o llamarnos al {phone}.
          </p>
        </Section>

        <Section icon="fa-id-card" title="Datos personales que recabamos">
          <p>Según tu relación con INCAP, podemos recabar:</p>
          <List
            items={[
              "Aspirantes y alumnos: nombre completo, CURP, género, fecha de nacimiento, correo electrónico, teléfono, domicilio y datos de contacto de emergencia o de tu tutor.",
              "Documentación escolar: acta de nacimiento, certificados y constancias de estudios, comprobante de domicilio, fotografías para credenciales y documentos oficiales, calificaciones, asistencia y trámites de certificación y titulación.",
              "Datos de pago: conceptos, montos, fechas y comprobantes de los pagos de colegiaturas y servicios. No almacenamos números completos de tarjetas bancarias.",
              "Docentes: nombre, datos de contacto, currículum, grado académico, títulos y cédulas profesionales, y documentación para su contratación.",
              "Personas interesadas en nuestra oferta: nombre, teléfono, correo y programa de interés que nos compartes por el sitio, redes sociales, WhatsApp o en nuestros planteles.",
              "Datos de uso de la plataforma Aula Virtual: usuario, actividades entregadas, participaciones y registros de acceso.",
            ]}
          />
          <p>
            No solicitamos datos personales sensibles. Si alguna vez fuera indispensable, te lo pediremos de forma
            expresa y por escrito, explicándote para qué.
          </p>
        </Section>

        <Section icon="fa-list-check" title="Finalidades del tratamiento">
          <p><strong>Finalidades necesarias</strong> para la relación que tienes con nosotros:</p>
          <List
            items={[
              "Proceso de admisión, inscripción, reinscripción y control escolar.",
              "Prestación del servicio educativo, incluida la plataforma Aula Virtual.",
              "Trámites ante autoridades educativas: validación de estudios, certificación, titulación y registro de cédulas.",
              "Cobro y facturación de colegiaturas y servicios.",
              "Avisos académicos y administrativos (por ejemplo, entregas pendientes, calificaciones o fechas importantes) por correo electrónico, teléfono, la propia plataforma o WhatsApp.",
              "Evaluación de la calidad docente y atención de dudas, quejas y sugerencias.",
              "Contratación y administración del personal docente.",
            ]}
          />
          <p><strong>Finalidades adicionales</strong>, que no son necesarias para el servicio:</p>
          <List
            items={[
              "Enviarte información sobre nuevos programas, promociones y eventos.",
              "Encuestas de satisfacción y estadísticas internas.",
            ]}
          />
          <p>
            Si no quieres que usemos tus datos para las finalidades adicionales, escríbenos a {email} en cualquier
            momento. Negarte no afectará el servicio que recibes.
          </p>
        </Section>

        <Section icon="fa-share-nodes" title="Transferencias y proveedores">
          <p>
            Podemos transferir tus datos, sin requerir tu consentimiento, a las autoridades educativas federales y
            estatales para validar estudios y emitir certificados, títulos y cédulas, así como a autoridades que lo
            requieran conforme a la ley.
          </p>
          <p>
            Para operar nos apoyamos en proveedores que tratan datos por nuestra cuenta y bajo nuestras instrucciones:
            alojamiento de servidores, correo electrónico y mensajería, incluido el servicio WhatsApp Business de Meta
            Platforms para enviar avisos a quienes aceptaron recibirlos. Estos proveedores no pueden usar tus datos para
            fines propios.
          </p>
          <p>No vendemos ni compartimos tus datos personales con terceros para fines comerciales.</p>
        </Section>

        <Section icon="fa-scale-balanced" title="Tus derechos ARCO y la revocación del consentimiento">
          <p>
            Tienes derecho a <strong>acceder</strong> a tus datos, <strong>rectificarlos</strong> si son inexactos,{" "}
            <strong>cancelarlos</strong> cuando consideres que no se requieren y <strong>oponerte</strong> a su uso para
            fines específicos. También puedes revocar el consentimiento que nos hayas dado.
          </p>
          <p>Para ejercerlos, envía una solicitud a {email} que incluya:</p>
          <List
            items={[
              "Tu nombre completo y un medio para comunicarte la respuesta.",
              "Copia de una identificación oficial o, si actúas en nombre de otra persona, el documento que acredite tu representación.",
              "La descripción clara de los datos y del derecho que quieres ejercer.",
            ]}
          />
          <p>
            Te responderemos en los plazos que establece la ley. Algunos datos no podrán cancelarse cuando debamos
            conservarlos por obligaciones legales, por ejemplo el expediente y el historial académico.
          </p>
        </Section>

        <Section icon="fa-child" title="Menores de edad">
          <p>
            Cuando el alumno sea menor de edad, el tratamiento de sus datos requiere el consentimiento de su madre, padre
            o tutor, quien podrá ejercer por él los derechos descritos en este aviso.
          </p>
        </Section>

        <Section icon="fa-cookie-bite" title="Cookies y tecnologías similares">
          <p>
            Este sitio no utiliza cookies ni tecnologías de rastreo con fines publicitarios. La plataforma Aula Virtual
            guarda en tu navegador únicamente la información necesaria para mantener tu sesión iniciada.
          </p>
        </Section>

        <Section icon="fa-rotate" title="Cambios a este aviso">
          <p>
            Podemos actualizar este aviso de privacidad. Publicaremos cualquier cambio en esta página, en{" "}
            <Link className="text-primary font-medium" to="/aviso-de-privacidad">incap.edu.mx/aviso-de-privacidad</Link>.
          </p>
          <p>
            Si consideras que tu derecho a la protección de datos personales ha sido vulnerado, puedes acudir ante la
            autoridad competente en materia de protección de datos personales.
          </p>
          <p className="text-sm text-tPrimary/60">Última actualización: {ULTIMA_ACTUALIZACION}.</p>
        </Section>
      </div>
    </div>
  );
};

export default AvisoPrivacidad;
